import * as api from "./level-test.api";
import * as mocks from "./level-test.mocks";
import config from "@/config/config";
import type { 
  StartLevelTestRequest,
  StartLevelTestResponse,
  SubmitAnswerRequest,
  SubmitAnswerResponse,
  GetLevelTestStatusRequest,
  GetLevelTestStatusResponse,
  GetLevelTestResultRequest,
  GetLevelTestResultResponse,
  GetSessionSnapshotResponse,
  GetNextQuestionResponse,
} from "./level-test.interfaces";

/**
 * Service selector para Level Test
 * Implementa o padrão de 4 arquivos das convenções do projeto
 * Seleciona automaticamente entre implementação real (API) ou mock baseado em VITE_USE_REAL_API
 */

/**
 * Iniciar teste de nivelamento (bootstrap)
 * Em modo real: usa snapshot + next-question para iniciar a sessão no FE
 * Em modo mock: usa mockStartLevelTest (fluxo simplificado)
 */
export const startLevelTest = async (
  request: StartLevelTestRequest
): Promise<StartLevelTestResponse> => {
  // Modo mock: usar função mock tradicional
  if (!config.useRealApi) {
    return mocks.mockStartLevelTest(request);
  }

  // Modo real: obter snapshot e próxima questão
  const snapshot = await api.apiGetSessionSnapshot();
  const next = await api.apiGetNextQuestion();

  if (next.isTestCompleted || !next.question) {
    // Sessão sem próxima questão: retornar estrutura mínima
    return {
      sessionId: snapshot.sessionId,
      firstQuestion: {
        id: "__none__",
        questionText: "",
        questionType: "multiple_choice",
        options: [],
        difficultyLevel: 0,
        skillArea: "general",
      },
    };
  }

  return {
    sessionId: snapshot.sessionId,
    firstQuestion: next.question,
  };
};

/**
 * Submeter resposta e obter próxima questão
 */
export const submitAnswer = async (
  request: SubmitAnswerRequest
): Promise<SubmitAnswerResponse> => {
  return config.useRealApi
    ? api.apiSubmitAnswer(request)
    : mocks.mockSubmitAnswer(request);
};

/**
 * Obter status atual do teste de nivelamento
 */
export const getLevelTestStatus = async (
  request: GetLevelTestStatusRequest
): Promise<GetLevelTestStatusResponse> => {
  return config.useRealApi
    ? api.apiGetLevelTestStatus(request)
    : mocks.mockGetLevelTestStatus(request);
};

/**
 * Obter resultado final do teste de nivelamento
 */
export const getLevelTestResult = async (
  request: GetLevelTestResultRequest
): Promise<GetLevelTestResultResponse> => {
  return config.useRealApi
    ? api.apiGetLevelTestResult(request)
    : mocks.mockGetLevelTestResult(request);
};

/**
 * Abortar teste de nivelamento em andamento
 */
export const abortLevelTest = async (sessionId: string): Promise<void> => {
  return config.useRealApi
    ? api.apiAbortLevelTest(sessionId)
    : mocks.mockAbortLevelTest(sessionId);
};

/** Serviços adicionais para snapshot e next-question **/
export const getSessionSnapshot = async (): Promise<GetSessionSnapshotResponse> => {
  console.log("🔍 [SERVICE] getSessionSnapshot - Starting call, useRealApi:", config.useRealApi);
  
  const result = config.useRealApi ? api.apiGetSessionSnapshot() : mocks.mockGetSessionSnapshot();
  
  result.then(response => {
    console.log("🔍 [SERVICE] getSessionSnapshot - Response received:", {
      sessionId: response.sessionId,
      answeredCount: response.answered.length,
      isTestCompleted: response.isTestCompleted,
      answeredItems: response.answered.map(item => ({
        order: item.order,
        questionId: item.question.id,
        questionText: item.question.questionText,
        optionsCount: item.question.options?.length || 0
      }))
    });
  }).catch(error => {
    console.error("🔍 [SERVICE] getSessionSnapshot - Error:", error);
  });
  
  return result;
};

export const getNextQuestion = async (): Promise<GetNextQuestionResponse> => {
  console.log("🔍 [SERVICE] getNextQuestion - Starting call, useRealApi:", config.useRealApi);
  
  const result = config.useRealApi ? api.apiGetNextQuestion() : mocks.mockGetNextQuestion();
  
  result.then(response => {
    console.log("🔍 [SERVICE] getNextQuestion - Response received:", {
      sessionId: response.sessionId,
      questionNumber: response.questionNumber,
      isTestCompleted: response.isTestCompleted,
      question: response.question ? {
        id: response.question.id,
        questionText: response.question.questionText,
        questionType: response.question.questionType,
        optionsCount: response.question.options?.length || 0,
        options: response.question.options?.map(opt => ({
          id: opt.id,
          text: opt.text,
          order: opt.order
        })) || []
      } : null
    });
  }).catch(error => {
    console.error("🔍 [SERVICE] getNextQuestion - Error:", error);
  });
  
  return result;
};